import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { 
  NavigationTabs, 
  ContentStatsCards, 
  ContentLibraryHeader, 
  ContentTable 
} from "./components";
import { useContentManagement } from "../../../hooks/useContentManagement";
import { ContentManagementProvider } from "./ContentManagementContext";

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState("blog-posts");
  const [searchTerm, setSearchTerm] = useState("");
  const contentManager = useContentManagement();
  const { 
    error, 
    fetchContentMetrics, 
    fetchBlogPosts, 
    fetchFAQs,
    resetBlogFilters,
    resetFaqFilters
  } = contentManager;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchTerm(""); // Reset search when switching tabs
    
    // Reset filters when switching tabs
    if (tabId === "blog-posts") {
      resetBlogFilters();
    } else if (tabId === "faqs") {
      resetFaqFilters();
    }
  };

  // Show error state if there's an API error
  if (error) {
    return (
      <ContentManagementProvider value={contentManager}>
      <div className="min-h-screen p-4 sm:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading content management data
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => {
                    if (activeTab === "blog-posts") {
                      fetchContentMetrics();
                      fetchBlogPosts();
                    } else if (activeTab === "faqs") {
                      fetchContentMetrics();
                      fetchFAQs();
                    }
                  }}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </ContentManagementProvider>
    );
  }

  return (
    <ContentManagementProvider value={contentManager}>
    <div className="min-h-screen p-4 sm:p-6 space-y-6">
      {/* Navigation Tabs */}
      <NavigationTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Content Statistics Cards */}
      {["blog-posts", "faqs"].includes(activeTab) && (
        <ContentStatsCards activeTab={activeTab} />
      )}

      {/* Content Library Section */}
      {["blog-posts", "faqs"].includes(activeTab) ? (
        <div className="space-y-4">
          <ContentLibraryHeader activeTab={activeTab} searchTerm={searchTerm} onSearchChange={setSearchTerm} />
          <ContentTable activeTab={activeTab} searchTerm={searchTerm} />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 flex flex-col items-center text-center">
          <Star className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming soon</h3>
          <p className="text-gray-600">
            We&apos;re working on {activeTab.replace('-', ' ')}. Check back later.
          </p>
        </div>
      )}
    </div>
    </ContentManagementProvider>
  );
};

export default ContentManagement;
